import { Injectable } from '@angular/core';
import type { UserDropdownDto } from '@zaoblako/shared/modules/security/dtos/user/user-dropdown.dto';
import type { UserDropdownListFindDto } from '@zaoblako/shared/modules/security/dtos/user/user-dropdown-list-find.dto';
import { UserStateService } from '@zaoblako/web/core/services/security/user-state.service';
import { isNil } from 'lodash';
import type { Observable } from 'rxjs';
import { of } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';

interface UserSearchLoadedStateInterface {
  items: UserDropdownDto[];
  count: number;
  nextOffset: number;
}

/**
 * Общий кэш пользователей для dropdown: одна страница поиска = один HTTP,
 * сущности копятся по id и переиспользуются между селектами.
 */
@Injectable({
  providedIn: 'root'
})
export class UserSearchCacheService {

  public readonly pageSize = 20;

  private readonly byId = new Map<number, UserDropdownDto>();
  private readonly pages = new Map<string, Observable<UserDropdownListFindDto>>();
  private readonly loadedBySearch = new Map<string, UserSearchLoadedStateInterface>();

  public constructor(private readonly userStateService: UserStateService) {
  }

  /**
   * Возвращает страницу поиска (из кэша или с API) и дополняет общий пул пользователей.
   *
   * @param search — строка поиска
   * @param offset — смещение
   * @returns страница dropdown
   */
  public findPage(search: string, offset: number): Observable<UserDropdownListFindDto> {
    const normalizedSearch = search || '';
    const key = `${normalizedSearch}|${offset}`;
    let page$ = this.pages.get(key);

    if (!page$) {
      page$ = this.userStateService.findDropdown({
        search: normalizedSearch,
        limit: this.pageSize,
        offset,
      }).pipe(
        tap((result) => this.rememberPage(normalizedSearch, offset, result)),
        shareReplay(1),
      );
      this.pages.set(key, page$);
    }

    return page$;
  }

  /**
   * Уже загруженные для поиска пользователи (для мгновенного заполнения второго селекта).
   *
   * @param search — строка поиска
   * @returns накопленные items / count / nextOffset или null
   */
  public getLoadedState(search: string): UserSearchLoadedStateInterface | null {
    return this.loadedBySearch.get(search || '') || null;
  }

  /**
   * Достаёт пользователей по id из кэша, недостающих догружает через includeIds.
   *
   * @param ids — id пользователей
   * @returns список пользователей
   */
  public findByIds(ids: number[]): Observable<UserDropdownDto[]> {
    const uniqueIds = [...new Set(ids.map(Number).filter((id) => !Number.isNaN(id)))];
    const cached = uniqueIds
      .map((id) => this.byId.get(id))
      .filter(Boolean);
    const missingIds = uniqueIds.filter((id) => !this.byId.has(id));

    if (!missingIds.length) {
      return of(cached);
    }

    return this.userStateService.findDropdown({
      includeIds: missingIds,
      limit: missingIds.length,
      offset: 0,
    }).pipe(
      tap((result) => this.rememberUsers(result.items || [])),
      map(() => uniqueIds
        .map((id) => this.byId.get(id))
        .filter((user): user is UserDropdownDto => !isNil(user))),
    );
  }

  /**
   * Сохраняет страницу в общий пул и в накопленный список поиска.
   *
   * @param search — строка поиска
   * @param offset — смещение страницы
   * @param result — ответ API
   */
  private rememberPage(search: string, offset: number, result: UserDropdownListFindDto): void {
    const page = result.items || [];
    this.rememberUsers(page);

    const prev = this.loadedBySearch.get(search);
    const merged = this.mergeUnique(prev?.items || [], page);
    const nextOffset = Math.max(prev?.nextOffset || 0, offset + page.length);

    this.loadedBySearch.set(search, {
      items: merged,
      count: result.count || 0,
      nextOffset,
    });
  }

  /**
   * Кладёт пользователей в map по id.
   *
   * @param users — пользователи
   */
  private rememberUsers(users: UserDropdownDto[]): void {
    (users || []).forEach((user) => {
      if (!isNil(user?.id)) {
        this.byId.set(Number(user.id), user);
      }
    });
  }

  /**
   * Объединяет списки без дублей по id.
   *
   * @param lists — списки
   * @returns уникальный список
   */
  private mergeUnique(...lists: UserDropdownDto[][]): UserDropdownDto[] {
    const map = new Map<number, UserDropdownDto>();

    lists.forEach((list) => {
      (list || []).forEach((user) => {
        if (!isNil(user?.id)) {
          map.set(Number(user.id), user);
        }
      });
    });

    return Array.from(map.values());
  }
}
